// Store follow state read back from the query cache (M-D9: follow is signed-in only). A follow button can sit
// on the store page, a store list or rail, the Discover studio row or a product page, and the optimistic
// patch updates every one of those, so the best known state is whichever trustworthy cached copy exists.
import type { QueryClient } from "@tanstack/react-query";
import { qk } from "@/api/queries/qk";
import type { Paged, Store } from "@/api/types";

function storeIn(value: unknown, slug: string): Store | undefined {
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) return value.find((item: Store) => item?.slug === slug && "isFollowing" in item);
  const record = value as Partial<Store> & Partial<Paged<Store>> & { store?: Store };
  if (Array.isArray(record.items)) return storeIn(record.items, slug);
  if (record.slug === slug && typeof record.isFollowing === "boolean") return record as Store;
  if (record.store?.slug === slug) return record.store;
  return undefined;
}

/**
 * The best known copy of the store — the detail cache, else any other cached copy (including optimistic patches)
 * that isn't waiting on a refetch — or `undefined` when no trustworthy copy exists.
 *
 * A session change resets the detail and invalidates the lists (session.ts), so an invalidated list may still hold
 * the previous viewer's value — after sign-in, the anonymous `false` — and is skipped rather than trusted.
 */
export function knownStore(queryClient: QueryClient, slug: string): Store | undefined {
  const detail = queryClient.getQueryData<Store>(qk.stores.detail(slug));
  if (detail) return detail;
  const candidates = [
    ...queryClient.getQueryCache().findAll({ queryKey: qk.stores.all() }),
    ...queryClient.getQueryCache().findAll({ queryKey: ["products", "detail"] }),
  ];
  for (const query of candidates) {
    if (query.state.isInvalidated) continue;
    const found = storeIn(query.state.data, slug);
    if (found) return found;
  }
  return undefined;
}

/** The best known follow state (see `knownStore`), or `undefined` when no trustworthy copy exists. */
export function currentIsFollowing(queryClient: QueryClient, slug: string): boolean | undefined {
  return knownStore(queryClient, slug)?.isFollowing;
}
